import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { CurrencyDollar, TrendUp, Fire } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

const CATEGORIES = ["All", "Foundation Models", "AI Coding", "AI Chips", "AI Audio", "AI Agents", "AI Infrastructure", "AI Image", "AI Video", "AI Search"];

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-200 animate-pulse">
      <div className="w-8 h-6 bg-slate-200 rounded" />
      <div className="w-9 h-9 bg-slate-200 rounded-xl shrink-0" />
      <div className="flex-1">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
        <div className="h-3 bg-slate-200 rounded w-1/2" />
      </div>
      <div className="text-right">
        <div className="h-5 bg-slate-200 rounded w-16 mb-1" />
        <div className="h-3 bg-slate-200 rounded w-12" />
      </div>
    </div>
  );
}

function FundingRow({ round, rank }) {
  const slug = round.slug || round.id;
  const catColors = {
    "Foundation Models": "bg-blue-50 text-blue-700",
    "AI Coding":         "bg-emerald-50 text-emerald-700",
    "AI Chips":          "bg-purple-50 text-purple-700",
    "AI Audio":          "bg-amber-50 text-amber-700",
    "AI Agents":         "bg-cyan-50 text-cyan-700",
    "AI Infrastructure": "bg-indigo-50 text-indigo-700",
    "AI Image":          "bg-rose-50 text-rose-700",
    "AI Video":          "bg-pink-50 text-pink-700",
    "AI Search":         "bg-teal-50 text-teal-700",
  };

  return (
    <Link to={`/trending-ai/funding/${slug}`} className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-sm ${round.isNew ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-slate-200"} hover:border-blue-300`}>
      <div className="w-8 text-center shrink-0">
        <span className={`text-base font-black ${rank <= 3 ? "text-blue-600" : "text-slate-300"}`}>{rank}</span>
      </div>
      <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
        <CurrencyDollar size={17} className="text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-slate-900">{stripHtml(round.company)}</span>
          {(round.isNew || round.is_new) && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
          {round.hot && (
            <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
              <Fire size={7} weight="fill" /> HOT
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {round.category && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${catColors[round.category] || "bg-slate-100 text-slate-600"}`}>
              {round.category}
            </span>
          )}
          <span className="text-xs text-slate-400">{round.round}</span>
          {round.leadInvestors && round.leadInvestors.length > 0 && (
            <>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">{round.leadInvestors.join(", ")}</span>
            </>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-lg font-black text-emerald-600">{round.amount}</div>
        {round.valuation && <div className="text-xs text-slate-400">val. {round.valuation}</div>}
        {round.date && (
          <div className="text-[10px] text-slate-400">
            {new Date(round.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function AIFundingPage() {
  const [cat, setCat] = useState("All");

  const fetchFn = useCallback(
    (page) => fetchAISection("funding", { page, page_size: 20 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-funding",
    getCacheKey: (page) => `ai-funding-p${page}`,
    ttlMs: 10 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((f) => cat === "All" || f.category === cat)
    .sort((a, b) => (b.amountNum || 0) - (a.amountNum || 0));

  const total = allItems.reduce((s, f) => s + (f.amountNum || 0), 0);
  const recentTotal = allItems.filter((f) => f.isNew || f.is_new).reduce((s, f) => s + (f.amountNum || 0), 0);
  const largest = allItems.sort((a, b) => (b.amountNum || 0) - (a.amountNum || 0))[0];

  return (
    <>
      <SEO
        title="AI Funding — Investment Rounds, Valuations & Investors · TrendMing"
        description="Track AI funding rounds: Anthropic, Cursor, ElevenLabs, Groq, and more. Amounts, investors, valuations, and deal analysis."
        canonical="https://trendming.com/trending-ai/funding"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Funding</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Funding Tracker</h1>
          <p className="text-slate-500 text-sm mt-1">Real-time tracking of AI investment rounds, valuations, and funding milestones.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-2 mb-2">
              <TrendUp size={16} weight="fill" />
              <span className="text-xs font-semibold uppercase tracking-wide opacity-80">Total Tracked</span>
            </div>
            <div className="text-3xl font-black">${(total / 1000).toFixed(1)}B</div>
            <div className="text-sm opacity-70 mt-1">{allItems.length} funding rounds</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Recent Rounds</div>
            <div className="text-3xl font-black text-slate-900">${(recentTotal / 1000).toFixed(1)}B</div>
            <div className="text-sm text-slate-400 mt-1">{allItems.filter((f) => f.isNew || f.is_new).length} new this month</div>
          </div>
          {largest && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Largest Round</div>
              <div className="text-3xl font-black text-slate-900">{largest.amount}</div>
              <div className="text-sm text-slate-400 mt-1">{largest.company} · {largest.round}</div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
          {!loading && filtered.map((round, i) => (
            <FundingRow key={round.id} round={round} rank={i + 1} />
          ))}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No funding rounds found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4 mt-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load funding rounds</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}

        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
          <h3 className="text-base font-bold text-slate-900 mb-2">Track AI Startups</h3>
          <p className="text-sm text-slate-500 mb-4">Explore the companies behind these funding rounds and their products.</p>
          <a href="/trending-ai/startups" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm rounded-xl transition-colors">
            View AI Startups
          </a>
        </div>
      </div>
    </>
  );
}
